import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { DjaySpecimen } from '../../../mascot';
import type { DjayEmotion, DjayVariant, DjayAccessory } from '../../../mascot';
import type { AchievementRarity } from '../../../mascot/achievements/achievementData';
import { useMotionBudget } from '../../../app/hooks/useMotionBudget';
interface RarityDjayWrapperProps {
  rarity: AchievementRarity;
  emotion: DjayEmotion;
  variant?: DjayVariant;
  accessory?: DjayAccessory;
  isUnlocked: boolean;
  animate: boolean;
}
// Compact cards always render Djay at the small size.
const BASE_SIZE = 'sm' as const;
/**
 * RarityDjayWrapper — wraps a Djay specimen in rarity-specific visual treatment.
 *
 * Intent B (specimen): the card shows the REWARD look, not the learner's
 * selected look. Using <DjaySpecimen> makes that deliberate difference explicit
 * (see MASCOT_SSOT_ARCHITECTURE_AUDIT_2026 §5.2). This renderer was lifted out
 * of the section god-file per §4.2 so the specimen-vs-identity intent stays
 * isolated and the section file no longer owns mascot-rendering logic.
 */
export function RarityDjayWrapper({
  rarity,
  emotion,
  variant = 'default',
  accessory = 'none',
  isUnlocked,
  animate
}: RarityDjayWrapperProps) {
  const {
    shouldAnimate
  } = useMotionBudget();
  // Honour prefers-reduced-motion: the Djay specimen itself only animates when
  // the caller wants it AND the motion budget allows it; the rarity rings/crown
  // collapse to static treatments below.
  const specimenAnimate = animate && shouldAnimate;
  if (!isUnlocked) {
    return <div className="relative" data-id="element-1794">
        <div className="grayscale opacity-50" data-id="element-1795">
          <DjaySpecimen look={{
          variant: 'default',
          accessory: 'none'
        }} size={BASE_SIZE} emotion={emotion} animate={false} data-id="element-1796" />
        </div>
      </div>;
  }
  switch (rarity) {
    case 'legendary':
      return <div className="relative" data-id="element-1797">
          {/* Pulsing glow ring — static under reduced motion */}
          {shouldAnimate ? <motion.div className="absolute -inset-2 rounded-full bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400 opacity-40 blur-sm" aria-hidden="true" animate={{
          opacity: [0.3, 0.6, 0.3],
          scale: [1, 1.1, 1]
        }} transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1798" /> : <div className="absolute -inset-2 rounded-full bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400 opacity-35 blur-sm" aria-hidden="true" data-id="element-1799" />}
          {/* Crown overlay — floats only when motion is allowed */}
          {shouldAnimate ? <motion.div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20" aria-hidden="true" animate={{
          y: [0, -3, 0],
          rotate: [0, 5, -5, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1800">
              <Star className="w-5 h-5 text-amber-400 fill-amber-400 drop-shadow" data-id="element-1801" />
            </motion.div> : <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20" aria-hidden="true" data-id="element-1802">
              <Star className="w-5 h-5 text-amber-400 fill-amber-400 drop-shadow" data-id="element-1803" />
            </div>}
          <div className="relative z-10" data-id="element-1804">
            <DjaySpecimen look={{
            variant,
            accessory
          }} size={BASE_SIZE} emotion={emotion} animate={specimenAnimate} data-id="element-1805" />
          </div>
        </div>;
    case 'rare':
      return <div className="relative" data-id="element-1806">
          {/* Golden glow ring — static under reduced motion */}
          {shouldAnimate ? <motion.div className="absolute -inset-1.5 rounded-full bg-gradient-to-r from-amber-300 via-yellow-300 to-orange-300 opacity-50 blur-sm" aria-hidden="true" animate={{
          opacity: [0.4, 0.6, 0.4]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1807" /> : <div className="absolute -inset-1.5 rounded-full bg-gradient-to-r from-amber-300 via-yellow-300 to-orange-300 opacity-45 blur-sm" aria-hidden="true" data-id="element-1808" />}
          <div className="relative z-10" data-id="element-1809">
            <DjaySpecimen look={{
            variant,
            accessory
          }} size={BASE_SIZE} emotion={emotion} animate={specimenAnimate} data-id="element-1810" />
          </div>
          {/* Star sparkle — spins only when motion is allowed */}
          {shouldAnimate ? <motion.div className="absolute -top-1 -right-1 z-20" aria-hidden="true" animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 180, 360]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-1811">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" data-id="element-1812" />
            </motion.div> : <div className="absolute -top-1 -right-1 z-20" aria-hidden="true" data-id="element-1813">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" data-id="element-1814" />
            </div>}
        </div>;
    case 'uncommon':
      return <div className="relative" data-id="element-1815">
          {/* Subtle glow ring */}
          <div className="absolute -inset-1 rounded-full bg-emerald-300/30 blur-sm" aria-hidden="true" data-id="element-1816" />
          <div className="relative z-10" data-id="element-1817">
            <DjaySpecimen look={{
            variant,
            accessory
          }} size={BASE_SIZE} emotion={emotion} animate={specimenAnimate} data-id="element-1818" />
          </div>
        </div>;
    default:
      return <DjaySpecimen look={{
        variant,
        accessory
      }} size={BASE_SIZE} emotion={emotion} animate={specimenAnimate} data-id="element-1819" />;
  }
}